import { type ReactNode } from "react";

import CloseIcon from "@mui/icons-material/CloseRounded";
import { Box, CircularProgress, Divider, IconButton, Stack, Typography } from "@mui/material";
import { Container, type SxProps, type Theme } from "@mui/system";

import { HeaderTabNavigation } from "./HeaderTabNavigation";
import { type HeaderTabConfig } from "./types";

type Props = {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  headerElement?: ReactNode;
  sidePanelSx?: SxProps<Theme>;
  dataCy?: string;
  headerTabs?: HeaderTabConfig;
  loading?: boolean;
  maxHeight?: number | null;
};

const DEFAULT_WIDTH = 550;

export const SidePanel = ({
  open,
  title,
  onClose,
  children,
  width = DEFAULT_WIDTH,
  headerElement,
  sidePanelSx,
  dataCy,
  headerTabs,
  loading,
  maxHeight,
}: Props) => {
  const heightStyle = maxHeight ? `${maxHeight}px` : "auto";
  const dividerHeight = maxHeight ? `${maxHeight + 8}px` : "100%";
  return (
    <Box
      sx={{
        width: open ? width : 0,
        transition: "width 0.3s ease-in-out",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {open && (
        <>
          <Divider
            orientation="vertical"
            sx={{
              position: "absolute",
              height: dividerHeight,
              marginTop: -1,
              zIndex: 1,
            }}
          />
          <Box
            data-cy={dataCy}
            sx={{
              width,
              backgroundColor: "background.paper",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              maxHeight: heightStyle,
              position: "relative",
              ...sidePanelSx,
            }}
          >
            {(title || headerElement) && (
              <>
                <Stack
                  direction="row"
                  sx={{
                    py: 3,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    px: 4,
                    flexShrink: 0,
                  }}
                >
                  {title ? <Typography variant="h3">{title}</Typography> : <Box />}
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-end">
                    <Box sx={{ pt: 0.5 }}>{headerElement}</Box>
                    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                      <IconButton
                        data-cy="closeButton"
                        aria-label="Close"
                        onClick={onClose}
                        size="large"
                        sx={{ pt: 1, position: "relative", top: -6 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                </Stack>
                {headerTabs && <HeaderTabNavigation headerTabs={headerTabs} />}
                {!headerTabs && <Divider sx={{ mb: 1, flexShrink: 0 }} />}
              </>
            )}
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                minHeight: 0,
              }}
            >
              {loading && (
                <Container
                  maxWidth="sm"
                  sx={{
                    height: "70vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Container>
              )}
              {children}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
